// src/App.js 
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import CheckoutForm from './CheckoutForm';
import SubscriptionInfo from './SubscriptionInfo';
import LandingPage from './AlphaMessage';
import Footer from './Footer'; 
import PricingPage from './pages/PricingPage';
import FAQPage from './pages/FAQPage';
import DownloadPage from './pages/DownloadPage';
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import BlogPage from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SecurityPage from './pages/SecurityPage';
import ChangelogPage from './pages/ChangelogPage';
import NotFoundPage from './pages/NotFoundPage';
import MobileHeader from './mobile/MobileHeader';
import useOnboardingStatus, { apiUrl } from './hooks/useOnboardingStatus';
import AnalyticsConsent from './components/AnalyticsConsent';
import GoldThread from './components/GoldThread';
import { capture, CONSENT_EVENT } from './analytics';
import { resolveTheme } from './theme/resolveTheme';
import './styles.css';
import './polish.css';
import { Navigate, Routes, Route, useNavigate, useLocation } from 'react-router-dom';


const LIGHT_THEME_CHROME = '#eff0ee';
const DARK_THEME_CHROME = '#242033';
// The phone's ground colours (styles.css mobile block), so Safari's toolbar
// continues the page instead of sitting on it as a grey or purple band.
const LIGHT_THEME_CHROME_MOBILE = '#f4f0e9';
const DARK_THEME_CHROME_MOBILE = '#0f0f13';

function CheckoutWithStripe({ subscription, refreshOnboarding }) {
  const [stripePromise] = useState(() =>
    loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY),
  );

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        subscription={subscription}
        refreshOnboarding={refreshOnboarding}
      />
    </Elements>
  );
}

const isMobileViewportNow = () =>
  typeof window !== 'undefined' &&
  Boolean(window.matchMedia) &&
  window.matchMedia('(max-width: 768px)').matches;

const isMobileSafariBrowser = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;

  const ua = window.navigator.userAgent || '';
  const isAppleMobileDevice =
    /iP(hone|od|ad)/.test(ua) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  const isSafariBrowser =
    /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|SamsungBrowser/i.test(ua);

  return isAppleMobileDevice && isSafariBrowser && isMobileViewportNow();
};

const shouldUseLightSafariChromeScheme = (pathname) =>
  isMobileSafariBrowser() && pathname === '/';

const applyThemeToDom = (theme) => {
  if (typeof document === 'undefined') return;
  const isLight = theme === 'light';
  const root = document.documentElement;
  const body = document.body;
  const currentPathname =
    typeof window !== 'undefined' ? window.location.pathname || '/' : '/';
  const forceLightChromeScheme = shouldUseLightSafariChromeScheme(currentPathname);

  if (root) {
    root.classList.toggle('light-mode', isLight);
    root.setAttribute('data-theme', theme);
    root.style.colorScheme = isLight || forceLightChromeScheme ? 'light' : 'dark';
  }

  if (body) {
    body.classList.toggle('light-mode', isLight);
    body.setAttribute('data-theme', theme);
  }

  // Every theme-color meta, not just the id'd one: browsers use the first
  // matching one in tree order, and index.html carries media-gated fallbacks.
  const mobile = isMobileViewportNow();
  const chrome = isLight
    ? (mobile ? LIGHT_THEME_CHROME_MOBILE : LIGHT_THEME_CHROME)
    : (mobile ? DARK_THEME_CHROME_MOBILE : DARK_THEME_CHROME);
  document.querySelectorAll("meta[name='theme-color']").forEach((meta) => {
    meta.setAttribute('content', chrome);
  });
};

const isStandaloneDisplayMode = () => {
  if (typeof window === 'undefined') return false;
  const mediaStandalone = window.matchMedia
    ? window.matchMedia('(display-mode: standalone)').matches
    : false;
  const iosStandalone = window.navigator && window.navigator.standalone === true;
  return mediaStandalone || iosStandalone;
};

const applyDisplayModeToDom = () => {
  if (typeof document === 'undefined') return;
  const standalone = isStandaloneDisplayMode();
  const root = document.documentElement;
  const body = document.body;

  if (root) {
    root.classList.toggle('standalone-mode', standalone);
  }
  if (body) {
    body.classList.toggle('standalone-mode', standalone);
  }
};

const applyRoutePaletteToDom = (pathname) => {
  if (typeof document === 'undefined') return;
  const isHomeRoute = pathname === '/';
  const root = document.documentElement;
  const body = document.body;

  if (root) {
    root.classList.toggle('route-home', isHomeRoute);
    root.classList.toggle('route-subpage', !isHomeRoute);
  }
  if (body) {
    body.classList.toggle('route-home', isHomeRoute);
    body.classList.toggle('route-subpage', !isHomeRoute);
  }
};

const SITE_NAV = [
  { href: '/', label: 'Home' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/faq', label: 'FAQ' },
  { href: '/download', label: 'Download' },
];

// Desktop site header. Phones get MobileHeader instead; both are always
// rendered and CSS shows the right one, so the prerendered HTML fits any width.
function SiteHeader() {
  const { isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!headerRef.current) return;
      const y = window.scrollY || window.pageYOffset || 0;
      headerRef.current.classList.toggle('small', y > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="page-header" ref={headerRef}>
      <div className="header-content">
        <a className="header-logo" href="/">
          <img
            src="/icons/png/virgil-profile-mark.webp"
            alt="Virgil"
            className="header-logo-img"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
          <span className="header-logo-lockup">
            <span className="header-logo-wordmark">Virgil</span>
          </span>
        </a>

        <nav className="header-nav">
          {SITE_NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="nav-link"
              aria-current={location.pathname === item.href ? 'page' : undefined}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* The gold button stays the last item, signed in or out. */}
        <div className="header-actions">
          {!isAuthenticated ? (
            <>
              <button
                type="button"
                className="nav-button nav-button--text"
                onClick={() => loginWithRedirect({
                  appState: { returnTo: `${location.pathname}${location.search}` },
                  authorizationParams: { screen_hint: 'login', prompt: 'select_account' }
                })}
              >
                Log in
              </button>
              <button
                type="button"
                className="nav-button primary"
                onClick={() => navigate('/signup')}
              >
                Start 14-day trial
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="nav-button nav-button--text"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Log out
              </button>
              <button
                type="button"
                className="nav-button primary"
                onClick={() => navigate('/account')}
              >
                Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function RoutePending() {
  return (
    <div className="route-pending" role="status">
      <span className="route-pending-mark" aria-hidden="true" />
      <span className="visually-hidden">Loading</span>
    </div>
  );
}


// `hydrating`: the page was prerendered for an anonymous visitor (index.js),
// so render that view while Auth0 is still loading instead of a blank state.
// `initialMobileViewport`: lets the prerender build the phone variant.
function App({ hydrating = false, initialMobileViewport = false }) {
  const {
    isLoading,
    isAuthenticated,
    logout,
    user,
    getAccessTokenSilently,
  } = useAuth0();
  const location = useLocation();
  const onboarding = useOnboardingStatus();
  const theme = resolveTheme({ pathname: location.pathname });
  const [rendersBeforeAuth] = useState(Boolean(hydrating));
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return Boolean(initialMobileViewport);
    return window.matchMedia('(max-width: 768px)').matches;
  });

  useLayoutEffect(() => {
    applyThemeToDom(theme);
    applyDisplayModeToDom();
    applyRoutePaletteToDom(location.pathname);
  }, [theme, location.pathname]);

  // The first commit has replaced any prerendered HTML that was held back.
  useLayoutEffect(() => {
    document.documentElement.classList.remove('prerender-stale');
  }, []);

  useLayoutEffect(() => {
    if (!isLoading) document.documentElement.classList.remove('auth-pending');
  }, [isLoading]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const syncShell = () => {
      applyThemeToDom(theme);
      applyDisplayModeToDom();
      applyRoutePaletteToDom(window.location.pathname || location.pathname);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncShell();
    };
    const standaloneQuery = window.matchMedia
      ? window.matchMedia('(display-mode: standalone)')
      : null;

    window.addEventListener('pageshow', syncShell);
    window.addEventListener('focus', syncShell);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (standaloneQuery) {
      standaloneQuery.addEventListener
        ? standaloneQuery.addEventListener('change', syncShell)
        : standaloneQuery.addListener(syncShell);
    }

    return () => {
      window.removeEventListener('pageshow', syncShell);
      window.removeEventListener('focus', syncShell);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (standaloneQuery) {
        standaloneQuery.removeEventListener
          ? standaloneQuery.removeEventListener('change', syncShell)
          : standaloneQuery.removeListener(syncShell);
      }
    };
  }, [theme, location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => setIsMobileViewport(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    const captureAuthCompletion = () => {
      if (!isAuthenticated) return;
      try {
        if (sessionStorage.getItem('virgil_auth_completed') === 'true') return;
        if (capture('auth_completed')) {
          sessionStorage.setItem('virgil_auth_completed', 'true');
        }
      } catch {
        capture('auth_completed');
      }
    };
    captureAuthCompletion();
    window.addEventListener(CONSENT_EVENT, captureAuthCompletion);
    return () =>
      window.removeEventListener(CONSENT_EVENT, captureAuthCompletion);
  }, [isAuthenticated]);

  const authPending = isLoading && !rendersBeforeAuth;
  const useMobileLanding = !isAuthenticated && location.pathname === '/' && isMobileViewport;

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        const accessToken = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email delete:current_user',
        });
        const response = await fetch(apiUrl('/user/delete'), {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (response.ok) {
          alert('Your account has been deleted.');
          logout({ returnTo: window.location.origin });
        } else {
          const errorData = await response.json();
          console.error('Error deleting account:', errorData);
          alert('An error occurred while deleting your account.');
        }
      } catch (error) {
        console.error('Error deleting account:', error);
        alert('An error occurred while deleting your account.');
      }
    }
  };

  return (
    <div className={`app-wrapper ${useMobileLanding ? 'app-wrapper-mobile-landing' : ''}`}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <SiteHeader />
      <MobileHeader dock={useMobileLanding} />
      <GoldThread />
      <main id="main-content" tabIndex="-1">
      {authPending ? (
        <RoutePending />
      ) : !isAuthenticated ? (
        <Routes>
          <Route path="/" element={<LandingPage useMobileVersion={useMobileLanding} />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="/signup" element={<SignupRedirect />} />
          <Route path="/checkout" element={<SignupRedirect />} />
          <Route path="/account" element={<Navigate to="/" replace />} />
          <Route path="/success" element={<Navigate to="/" replace />} />
          <Route path="/cancel" element={<Navigate to="/" replace />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/alpha-access" element={<Navigate to="/signup" replace />} />
          <Route
            path="/download"
            element={
              <DownloadPage
                onboardingStatus={onboarding.status}
                subscription={onboarding.subscription}
                refreshOnboarding={onboarding.refresh}
              />
            }
          />
          <Route path="/vrgldl" element={<Navigate to="/download" replace />} />
          <Route path="/alpha.html" element={<Navigate to="/signup" replace />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      ) : !user?.email_verified ? (
        <div className="page-container">
          <h2>Please Verify Your Email</h2>
          <p>
            A verification email has been sent to {user.email}. Please verify your email to continue.
          </p>
          <button
            className="cta-button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Log Out
          </button>
        </div>
      ) : (
        <div className="page-container">
          <Routes>
            <Route
              path="/"
              element={
                onboarding.status === 'loading' || onboarding.status === 'idle' ? (
                  <div role="status">Checking your beta access…</div>
                ) : (
                  <Navigate
                    to={onboarding.status === 'eligible' ? '/download' : '/pricing'}
                    replace
                  />
                )
              }
            />
            <Route
              path="/pricing"
              element={
                <PricingPage
                  subscription={onboarding.subscription}
                />
              }
            />
            <Route path="/faq" element={<FAQPage />} />
            <Route
              path="/download"
              element={
                <DownloadPage
                  onboardingStatus={onboarding.status}
                  subscription={onboarding.subscription}
                  refreshOnboarding={onboarding.refresh}
                />
              }
            />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route
              path="/account"
              element={
                <Home
                  onDeleteAccount={handleDeleteAccount}
                  onboarding={onboarding}
                />
              }
            />
            <Route
              path="/checkout"
              element={
                <CheckoutWithStripe
                  subscription={onboarding.subscription}
                  refreshOnboarding={onboarding.refresh}
                />
              }
            />
            <Route path="/success" element={<Success />} />
            <Route path="/cancel" element={<Cancel />} />
            <Route path="/alpha-access" element={<Navigate to="/pricing" replace />} />
            <Route path="/alpha.html" element={<Navigate to="/pricing" replace />} />
            <Route path="/vrgldl" element={<Navigate to="/download" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      )}
      </main>
      <Footer />
      <AnalyticsConsent />
    </div>
  );
}

function Success() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState('finalizing');
  const [denialReason, setDenialReason] = React.useState(null);
  const [checkoutMode, setCheckoutMode] = React.useState(() =>
    typeof window !== 'undefined'
      ? localStorage.getItem('last_checkout_mode') || 'trial'
      : 'trial',
  );
  const search = window.location.search || '';
  const sessionId = new URLSearchParams(search).get('session_id') || (typeof window !== 'undefined' ? localStorage.getItem('last_checkout_session_id') : null);
  React.useEffect(() => {
    let cancelled = false;
    let timeoutId;
    const wait = (milliseconds) =>
      new Promise((resolve) => {
        timeoutId = window.setTimeout(resolve, milliseconds);
      });

    async function finalize() {
      try {
        if (!sessionId) { setStatus('missing'); return; }
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });
        for (let attempt = 0; attempt < 10 && !cancelled; attempt += 1) {
          const resp = await fetch(
            apiUrl('/payment/finalize-session'),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ sessionId }),
            },
          );
          const payload = await resp.json().catch(() => ({}));
          if (['account_blocked', 'cvc_check_failed', 'payment_review_required', 'temporary_email_not_allowed'].includes(payload.error)) {
            localStorage.removeItem('last_checkout_session_id');
            localStorage.removeItem('last_checkout_mode');
            setDenialReason(payload.error);
            setStatus('denied');
            return;
          }
          if (!resp.ok) throw new Error(payload.error || 'finalize_failed');
          setCheckoutMode(payload.checkoutMode || 'trial');
          if (payload.status === 'denied' || payload.error === 'trial_not_eligible') {
            localStorage.removeItem('last_checkout_session_id');
            localStorage.removeItem('last_checkout_mode');
            capture('trial_denied');
            setStatus('denied');
            return;
          }
          if (payload.ready) {
            localStorage.removeItem('last_checkout_session_id');
            localStorage.removeItem('last_checkout_mode');
            capture(
              payload.checkoutMode === 'paid'
                ? 'paid_subscription_started'
                : 'trial_started',
            );
            setStatus('ok');
            timeoutId = window.setTimeout(() => navigate('/download', { replace: true }), 500);
            return;
          }
          await wait(1200);
        }
        if (!cancelled) setStatus('pending');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    }
    finalize();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [getAccessTokenSilently, navigate, sessionId]);

  return (
    <div className="success-container">
      <h2>
        {status === 'denied'
          ? denialReason === 'temporary_email_not_allowed'
            ? 'Please use a permanent email address'
          : denialReason === 'cvc_check_failed'
            ? 'Check your card security code'
            : denialReason === 'payment_review_required'
              ? 'Your payment needs a review'
            : denialReason === 'account_blocked'
              ? 'Account access is paused'
              : 'This account is not eligible for another trial'
          : checkoutMode === 'paid'
            ? 'Confirming your subscription'
            : 'Confirming your trial'}
      </h2>
      <p>
        {status === 'finalizing'
          ? 'Activating your Virgil access…'
          : status === 'ok'
            ? checkoutMode === 'paid'
              ? 'Your subscription is active. Taking you to downloads…'
              : 'Your trial has started. Access is ready. Taking you to downloads…'
            : status === 'denied'
              ? denialReason === 'temporary_email_not_allowed'
                ? 'This trial was canceled because the email address uses a temporary-mail service. Contact support@virgil.com.ai for help getting started with a permanent email address.'
              : denialReason === 'cvc_check_failed'
                ? 'Your bank did not verify the card security code. This checkout was canceled. Return to pricing and try again with the correct code or another card.'
                : denialReason === 'payment_review_required'
                  ? 'Your bank did not verify the card security code. Contact Virgil support before trying another checkout so we can check whether a payment was taken and resolve it.'
                : denialReason === 'account_blocked'
                  ? 'Contact Virgil support for help with this account.'
                  : 'A verified email or payment method associated with this checkout has already been used for a trial. The new trial was canceled and no charge was made.'
              : status === 'pending'
                ? 'Stripe is still confirming your subscription. You can check the download page again in a moment.'
                : status === 'missing'
                  ? 'The checkout session ID is missing.'
                  : 'We could not confirm your subscription yet. Check your account again in a moment.'}
      </p>
      {(status === 'pending' || status === 'error') && (
        <button className="cta-button" type="button" onClick={() => navigate('/download')}>
          Go to downloads
        </button>
      )}
      {status === 'denied' && !['account_blocked', 'payment_review_required'].includes(denialReason) && (
        <button className="cta-button" type="button" onClick={() => navigate('/pricing')}>
          Return to pricing
        </button>
      )}
    </div>
  );
}

function Cancel() {
  const navigate = useNavigate();
  return (
    <div className="cancel-container">
      <h2>Checkout canceled</h2>
      <p>No subscription changes were made. You can choose a plan again when you are ready.</p>
      <button className="cta-button" type="button" onClick={() => navigate('/pricing')}>
        Back to pricing
      </button>
    </div>
  );
}

function SignupRedirect() {
  const { loginWithRedirect } = useAuth0();
  const location = useLocation();
  useEffect(() => {
    capture('signup_started');
    const requested = new URLSearchParams(location.search).get('returnTo');
    const fallback =
      location.pathname === '/checkout'
        ? `${location.pathname}${location.search}`
        : '/pricing';
    const returnTo =
      requested && requested.startsWith('/') && !requested.startsWith('//')
        ? requested
        : fallback;
    loginWithRedirect({
      appState: { returnTo },
      authorizationParams: { screen_hint: 'signup' },
    });
  }, [location.pathname, location.search, loginWithRedirect]);
  return (
    <div className="page-container">
      <p>Redirecting to sign up…</p>
    </div>
  );
}


function Home({ onDeleteAccount, onboarding }) {
  const navigate = useNavigate();
  const subscription = onboarding?.subscription || null;
  const hasActiveSubscription =
    ['active', 'trialing'].includes(subscription?.status);

  return (
    <div className="authenticated-home">
      <h2>Subscriptions</h2>
      {onboarding?.status === 'loading' || onboarding?.status === 'idle' ? (
        <div className="usage-row muted" role="status">
          Loading subscription…
        </div>
      ) : hasActiveSubscription ? (
        <SubscriptionInfo data={subscription} />
      ) : onboarding?.status === 'error' ? (
        <div className="usage-row error">
          {onboarding.error || 'Unable to load subscription details.'}
        </div>
      ) : (
        <div>
          <p>You don't have an active subscription yet.</p>
          <p>Subscribe now to start using Virgil's AI-powered screenwriting platform!</p>
          <div className="subscription-actions">
            <button className="action-button subscribe-button" onClick={() => navigate('/pricing')}>
              SUBSCRIBE NOW
            </button>
            <button 
              className="action-button delete-button" 
              onClick={onDeleteAccount}
            >
              DELETE ACCOUNT
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
